import React, { FC, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import usePagination from '@mui/material/usePagination';
import { ButtonStyled, PageButton, Wrapper } from './styles';
import { ArrowBackIcon, ArrowForwardIcon } from 'application/assets';
import { PaginationProps } from './modules';

/**
 * Functional component that renders a pagination component with previous and next buttons.
 *
 * @param pages - The total number of pages to paginate through.
 * @param setCurrentPage - A function that sets the current page number.
 * @param currentPage - The current page number being displayed.
 */
const Pagination: FC<PaginationProps> = ({
  pages,
  setCurrentPage,
  currentPage,
}) => {
  const { t } = useTranslation();
  const { items } = usePagination({
    count: pages,
    page: currentPage,
    onChange: (e, page) => {
      setCurrentPage(page);
    },
  });

  // Find the previous button item
  const prevButton = useMemo(
    () => items.find((item) => item.type === 'previous'),
    [items, currentPage],
  );

  // Find the next button item
  const nextButton = useMemo(
    () => items.find((item) => item.type === 'next'),
    [items, currentPage],
  );

  // Filter out the previous and next buttons from the page list items
  const pageList = useMemo(
    () =>
      items.filter((item) => item.type !== 'next' && item.type !== 'previous'),
    [items, currentPage],
  );

  return (
    <Wrapper className="pagination-wrapper">
      <ButtonStyled {...prevButton!} type="button">
        <ArrowBackIcon /> {t('global.back')}
      </ButtonStyled>{' '}
      <div>
        {pageList.map(({ page, type, selected, ...item }, index) => {
          let children = null;
          if (type === 'start-ellipsis' || type === 'end-ellipsis') {
            children = '…';
          } else if (type === 'page') {
            children = (
              <PageButton currentPage={selected} type="button" {...item}>
                {page}
              </PageButton>
            );
          }
          return <div key={index}>{children}</div>;
        })}
      </div>
      <ButtonStyled {...nextButton} type="button">
        {t('global.next')} <ArrowForwardIcon />
      </ButtonStyled>
    </Wrapper>
  );
};

export default memo(Pagination);
